import { BasicFile, FileService, FileUploaderService, MaxFileSizeOptions, PredefinedFile } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { ChangeEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { SECURE_IMAGE_EXTENSIONS } from '../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../constants/application-endpoints';
import { FileProviderData } from '../constants/file-provider-enum';
import { FileUploaderRules } from 'src/interfaces/file-uploader-rules';

export interface MandatoryAttachmentFieldProps {
  attribute: string,
  key: string,
  required: boolean,
  value: string,
}

interface ProfileUploadModalProps extends Record<string, Value> {
  bodyToUpdate: BodyProps;
  mandatoryAttachmentsFields?: MandatoryAttachmentFieldProps[];
  onAbortProfileModal?: () => void;
  onConfirmUpload: (event: SubmitEvent) => void;
}

interface PredefinedFileValidKeys {
  key: string;
  required: boolean;
  value: string;
}

interface RequestPersonFilesSave {
  filename: string;
  filepath: string;
}

interface FieldTypes {
  dsValorNew: string | Value;
  dsValorOld: string | Value;
  nmAtributo: string;
  nrSeqDocumento?: string | Value;
  requiredDocKey?: string;
}

interface BodyProps {
  campos: FieldTypes[],
}

interface FileUploadedServerResponse {
  fileName: string;
  path: string;
}

interface RequestSavePersonDocument {
  filepath: string;
  ieAttribute: string;
  mandatoryDocId: string;
}

interface PersonDocumentResponse {
  filepath: string;
  nrSequence: number;
}

export default class ProfileModalUploadService {

  private bodyToUpdate!: BodyProps;
  private hasMissingFiles!: boolean;
  private uploadedFiles!: BasicFile[];
  private predefinedFiles!: PredefinedFileValidKeys[];
  private readonly MODAL_ID = 'a79e2f1c-3049-43a8-ae28-960aa49f806c';
  private onAbortProfileModal?: () => void;
  private onConfirmUpload: ((event: SubmitEvent) => void) | undefined;
  private static _instance: ProfileModalUploadService;

  private constructor() {
    // This is intentional
  }

  public static open(props: ProfileUploadModalProps): void {

    if (!ProfileModalUploadService._instance) {
      ProfileModalUploadService._instance = new ProfileModalUploadService();
    }

    ProfileModalUploadService._instance.openUploadModal(props);
  }

  private openUploadModal = ({
    bodyToUpdate,
    mandatoryAttachmentsFields,
    onConfirmUpload,
    onAbortProfileModal
  }: ProfileUploadModalProps): void => {
    this.uploadedFiles = [];
    this.hasMissingFiles = true;
    this.bodyToUpdate = bodyToUpdate;
    this.predefinedFiles = this.constructPredefinedFiles(mandatoryAttachmentsFields) || [];
    this.onConfirmUpload = onConfirmUpload;
    this.onAbortProfileModal = onAbortProfileModal;

    HttpService.get<FileUploaderRules>(APPLICATION_ENDPOINTS.GET_FILES_RULES, {}).then(response => {
      const fileUploaderRules = response.body;

      let fileSize;
      if (fileUploaderRules?.fileSize) {
        fileSize = {
          maxSize: fileUploaderRules.fileSize,
          sizeUnit: 'MB',
        } as MaxFileSizeOptions;
      }

      FileUploaderService.open({
        allowedTypes: [...SECURE_IMAGE_EXTENSIONS],
        fileHandlerInstance: FileService.getFileHandlerInstance(FileProviderData.fileProvider),
        id: this.MODAL_ID,
        maxFileSize: fileSize,
        onBeforeClose: this.onBeforeCloseModal,
        onChange: this.handleFileUploaderChange,
        onRequiredFileError: this.onRequiredFileError,
        predefinedFiles: (this.predefinedFiles as unknown as PredefinedFile[]),
      });
    });
  }

  private onBeforeCloseModal = (): void => {
    const { hasMissingFiles, onAbortProfileModal } = this;

    if (hasMissingFiles && onAbortProfileModal) {
      onAbortProfileModal();
    } else if (!hasMissingFiles) {
      this.onSubmitUploadModal(hasMissingFiles);
    }
  }

  private constructPredefinedFiles(mandatoryAttachmentsFields?: MandatoryAttachmentFieldProps[]): PredefinedFileValidKeys[] {
    if (!mandatoryAttachmentsFields) {
      return [];
    }

    return mandatoryAttachmentsFields?.map<PredefinedFileValidKeys>(item => {
      return {
        key: `${item.key};${item.attribute}`,
        required: item.required,
        value: item.value
      }
    });
  }

  /**
   * This method is executed by the FileUploaderService whenever the modal's submit button is clicked.
   * @param hasMissingFiles boolean
   */
  private onRequiredFileError = (hasMissingFiles: boolean): void => {
    this.hasMissingFiles = hasMissingFiles;
  }

  private onSubmitUploadModal = (hasMissingFiles: boolean): void => {
    const onConfirmFunction = this.onConfirmUpload;
    if (!onConfirmFunction || hasMissingFiles || !this.uploadedFiles.length) {
      return;
    }

    this.predefinedFiles?.forEach(predefinedFile => {
      this.bodyToUpdate.campos.forEach(row => {
        const predefinedFileProps = predefinedFile.key.split(';');
        if(row.nmAtributo.toUpperCase().includes(predefinedFileProps[1].toUpperCase().replace(/_/g, ''))) {
          row.requiredDocKey = predefinedFile.key;
        }
      });
    });

    const personFileDocuments = this.uploadedFiles.map<RequestPersonFilesSave>(file => {
      return {
        filename: file.filename,
        filepath: file.filepath
      }
    })

    this.uploadDocumentsToPersonFiles(personFileDocuments).then(bodyDocument => {
      this.savePersonDocument(bodyDocument).then(campos => {
        onConfirmFunction(new SubmitEvent({ record: { campos } }));
      });
    });
  }

  private uploadDocumentsToPersonFiles = (personFileDocuments: RequestPersonFilesSave[]): Promise<RequestSavePersonDocument[]> => {
    return HttpService
      .post(APPLICATION_ENDPOINTS.UPLOAD_PERSON_DOCUMENTS, {}, { body: (personFileDocuments as unknown as Record<string, Value>) })
      .then((responseData: HttpResponse<unknown>) => {
        const bodyResponseUpload = responseData.body as FileUploadedServerResponse[];

        this.uploadedFiles.forEach(file => {
          const finalFile = bodyResponseUpload.find(eachFinalPathFile => eachFinalPathFile.fileName === file.filename);
          file.filepath = finalFile?.path ?? '';
        });

        return this.uploadedFiles.map<RequestSavePersonDocument>(item => {
          if (!item.predefinedFileKey) {
            throw Error('One or more uploadedFiles items does not have the predefinedFileKey property');
          }

          const [key, attributeName] = item.predefinedFileKey.split(';');

          return {
            filepath: item.filepath,
            ieAttribute: attributeName,
            mandatoryDocId: key,
          }
        });
      });
  }

  private savePersonDocument = (bodyDocument: RequestSavePersonDocument[]): Promise<FieldTypes[]> => {
    return HttpService
      .post(APPLICATION_ENDPOINTS.SAVE_PERSON_DOCUMENTS_INFO_DB, {}, { body: (bodyDocument as unknown as Record<string, Value>) })
      .then((responseData: HttpResponse<unknown>) => {
        const documentsSaved = responseData.body as PersonDocumentResponse[];

        this.bodyToUpdate.campos.forEach((campo) => {
          const selectedFile = this.uploadedFiles.find(file => file.predefinedFileKey === campo.requiredDocKey);
          if (selectedFile) {
            const documentSaved = documentsSaved.find(document => document.filepath === selectedFile.filepath);
            campo.nrSeqDocumento = documentSaved ? documentSaved.nrSequence : null;
          }
        })

        return this.bodyToUpdate.campos;
      });
  }

  private handleFileUploaderChange = (event: ChangeEvent<BasicFile[]>): void => {
    this.uploadedFiles = event.value;
  }
}